import {
    Box,
    Button,
    Container,
    Header,
    SpaceBetween,
} from '@cloudscape-design/components';
import React, { useEffect, useRef } from 'react';
import { RecentlyUpdatedList } from 'src/components/RecentlyUpdatedList';
import {
    RecentlyUpdatedScope,
    useRecentlyUpdatedList,
    useUpdatesReadState,
} from 'src/hooks/useRecentlyUpdated';
import { useTranslate } from 'src/i18n/useTranslate';
import { AppLayoutWrapper } from './AppLayoutWrapper';

/**
 * Recently_Updated_Page — Slice 3 of the Recently_Updated_Notifications
 * feature. Renders two sections stacked, Personal_Updates_Section on top
 * and Global_Updates_Section below, each inside a Cloudscape `Container`
 * + `Header variant="h2"` (design.md § RecentlyUpdatedPage component).
 *
 * Each section fetches the shared Recently_Updated hooks with `limit=20`
 * (requirement 5.6 default page size) and renders:
 *   - the empty-state copy required by Requirements 5.3 / 5.4 (section
 *     header stays visible even when empty),
 *   - the shared `RecentlyUpdatedList` row component when non-empty,
 *   - a Cloudscape `Button` labelled "Load more" when the server returned
 *     a `nextCursor` so the user can advance the paginator.
 *
 * On mount this page fires `markRead()` exactly once per visit. The call
 * is fire-and-forget from the user's perspective (requirement 5.7) — a
 * rejection is caught, `console.error`'d and otherwise swallowed. A
 * `useRef` guard protects against React Strict Mode's double-invoke of
 * mount effects so the mark-read endpoint is only POSTed once.
 */

const PAGE_LIMIT = 20;

interface SectionProps {
    scope: RecentlyUpdatedScope;
    title: string;
    emptyText: string;
    loadMoreText: string;
    testIdPrefix: string;
}

const RecentlyUpdatedSection: React.FC<SectionProps> = ({
    scope,
    title,
    emptyText,
    loadMoreText,
    testIdPrefix,
}) => {
    const { data, nextCursor, loadMore, loading } = useRecentlyUpdatedList(
        scope,
        PAGE_LIMIT
    );

    const isEmpty = !loading && (!data || data.length === 0);

    return (
        <Container
            header={<Header variant="h2">{title}</Header>}
            data-testid={`${testIdPrefix}-section`}
        >
            <SpaceBetween size="m">
                {isEmpty ? (
                    <Box
                        variant="p"
                        data-testid={`${testIdPrefix}-empty`}
                        color="text-body-secondary"
                    >
                        {emptyText}
                    </Box>
                ) : (
                    <RecentlyUpdatedList entries={data ?? []} />
                )}
                {nextCursor ? (
                    <Button
                        onClick={() => {
                            void loadMore();
                        }}
                        disabled={loading}
                        data-testid={`${testIdPrefix}-load-more`}
                    >
                        {loadMoreText}
                    </Button>
                ) : null}
            </SpaceBetween>
        </Container>
    );
};

const RecentlyUpdatedPage: React.FC = () => {
    const t = useTranslate();
    const { markRead } = useUpdatesReadState();
    const markReadFiredRef = useRef(false);

    const pageTitle = t('recently-updated-page-title', 'Recently updated');
    const personalSectionTitle = t(
        'recently-updated-page-personal-section-title',
        'Updates to your documents'
    );
    const globalSectionTitle = t(
        'recently-updated-page-global-section-title',
        'All recent updates'
    );
    const personalEmpty = t(
        'recently-updated-page-personal-empty',
        'No recent updates on your favorited or recently-viewed documents.'
    );
    const globalEmpty = t(
        'recently-updated-page-global-empty',
        'No recent updates.'
    );
    const loadMoreText = t('recently-updated-page-load-more', 'Load more');

    useEffect(() => {
        // Guarded one-shot: React Strict Mode double-invokes mount effects
        // in development, so a plain `useEffect(..., [])` would POST
        // mark-read twice. The ref flips before the request issues so a
        // synchronous re-invoke sees the guard and bails. The ref is only
        // set once per component mount and intentionally not reset in the
        // cleanup — a re-mount should re-fire mark-read, but Strict Mode
        // only re-runs the effect without unmounting the component.
        if (markReadFiredRef.current) {
            return;
        }
        markReadFiredRef.current = true;
        // `markRead` already catches and logs internally (useRecentlyUpdated.ts),
        // but we repeat the catch here so the Promise chain can't leak an
        // unhandled rejection if the hook's implementation ever changes.
        // The user never sees an error for a failed mark-read
        // (requirement 5.7); the page continues to render regardless.
        Promise.resolve()
            .then(() => markRead())
            .catch((err) => {
                console.error(
                    '[RecentlyUpdated] mark-read on page mount failed',
                    { error: err }
                );
            });
        // Intentionally empty dep list — mark-read fires exactly once on
        // page visit (requirement 5.7). `markRead` is a stable callback
        // from the hook; depending on it would re-fire the effect across
        // renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const content = (
        <SpaceBetween size="l">
            <Header variant="h1">{pageTitle}</Header>
            <RecentlyUpdatedSection
                scope="personal"
                title={personalSectionTitle}
                emptyText={personalEmpty}
                loadMoreText={loadMoreText}
                testIdPrefix="recently-updated-personal"
            />
            <RecentlyUpdatedSection
                scope="global"
                title={globalSectionTitle}
                emptyText={globalEmpty}
                loadMoreText={loadMoreText}
                testIdPrefix="recently-updated-global"
            />
        </SpaceBetween>
    );

    return <AppLayoutWrapper content={content} />;
};

export default RecentlyUpdatedPage;
